import { userInitialState } from "../userReducer";
import { Mrz } from "../../passapp-sdk/models/Mrz";
import NewEvent from "../../passapp-sdk/newEvent";
import User from "../../passapp-sdk/user";
import { AuthorizationType, Config, GuestType, IAuthorization } from "../types/userTypes";
import Pass from "../../passapp-sdk/pass";
import { UserState } from "../../passapp-sdk/types/userTypes";

const user = new User();
const pass = new Pass();
const newEvent = new NewEvent();

export const findByDni = (
  token: string,
  dni: string,
  countryCode: string,
  dispatch: any,
  config?: Config
) => {
  user
    .findUserByDNI(token, dni, countryCode)
    .then((response) => {
      let initialData: UserState = {
        ...userInitialState,
        ...response.data,
      };
      createEvent(initialData, token, dispatch, config);
    })
    .catch((err) => {
      console.error(err);
    });
};

export const findByLastEntrance = (
  token: string,
  id: string,
  dni: string,
  countryCode: string,
  dispatch: any,
  config?: Config
) => {
  user
    .getLastEntrance(token, id, dni, countryCode, false)
    .then((response: any) => {
      const userData = response?.data?.user;
      if (!userData) {
        dispatch(null);
        return;
      }
      let initialData: UserState = {
        ...userInitialState,
        ...userData,
      };
      createEvent(initialData, token, dispatch, config);
    })
    .catch((err) => {
      console.error(err);
      dispatch(null);
    });
};

export const findByQR = (
  token: string,
  rawQr: string,
  dispatch: any,
  config?: Config,
  prefetchedData?: any,
) => {
  if (prefetchedData) {
    createEvent(prefetchedData, token, dispatch, config);
    return;
  }
  user
    .findUserByLPQR(token, rawQr)
    .then((response: any) => {
      createEvent(response.data, token, dispatch, config);
    })
    .catch((err) => {
      console.error(err);
    });
};

export const findByLPQR = (
  token: string,
  qr: string,
  dispatch: any,
  config?: Config,
  onError?: (err: any) => void
) => {
  user
    .findUserByLPQR(token, qr)
    .then((response: any) => {
      createEvent(response.data, token, dispatch, config);
    })
    .catch((err) => {
      console.error(err);
      if (onError) onError(err);
    });
};

export const findByMrz = (token: string, mrzData: Mrz, dispatch: any, config?: Config) => {
  user
    .findUserByMRZ(token, mrzData)
    .then((response) => {
      createEvent(response.data, token, dispatch, config);
    })
    .catch((err) => {
      console.error(`Error: ${err}`);
    });
};

export const findByPass = (
  token: string,
  country: any,
  userPass: { id: string; user: string },
  dispatch: any,
  config?: Config,
  prefetchedData?: any,
  rawPass?: string
) => {
  if (prefetchedData) {
    config = {
      ...config,
      userAddon: {
        ...config?.userAddon,
        isPass: prefetchedData.id,
        isUnknown:
          !(country === prefetchedData.country || country?.country === prefetchedData.country) ||
          false,
      },
    };
    const guest = prefetchedData.guests?.find((user: any) => user.id === userPass.user) || prefetchedData;
    createEvent(guest, token, dispatch, config);
    return;
  }
  pass
    .findPassByLPQR(token, rawPass!)
    .then((response: any) => {
      config = {
        ...config,
        userAddon: {
          ...config?.userAddon,
          isPass: response.data.id,
          isUnknown:
            !(country === response.data.country || country?.country === response.data.country) ||
            false,
        },
      };
      const guest = response.data.guests.find((user: any) => user.id === userPass.user);
      createEvent(guest, token, dispatch, config);
    })
    .catch((err) => {
      console.error(`Error: ${err}`);
    });
};

export const findByLPPass = (
  token: string,
  country: any,
  qr: string,
  dispatch: any,
  config?: Config,
  onError?: (err: any) => void,
  passUserId?: string
) => {
  pass
    .findPassByLPQR(token, qr)
    .then((response: any) => {
      let guest = response.data?.guest;
      if (!guest) {
        if (response.data?.guests && passUserId) {
          guest = response.data.guests.find((user: any) => user.id === passUserId);
        }
        if (!guest) {
          guest = response.data;
        }
      }
      const inviteId = response.data?.inviteId || response.data?.id || guest?.inviteId || guest?.id;
      const inviteCountry = response.data?.country || guest?.country || guest?.countryCode;
      config = {
        ...config,
        userAddon: {
          ...config?.userAddon,
          isPass: inviteId,
          isUnknown: !(country === inviteCountry || country?.country === inviteCountry) || false,
          passType: response.data?.type,
          passId: inviteId,
          passGuestsType: response.data?.guestsType,
          passHost: response.data?.host || response.data?.manager,
          passCompany: response.data?.company,
        },
      };
      createEvent(guest, token, dispatch, config);
    })
    .catch((err) => {
      console.error(`Error: ${err}`);
      if (onError) onError(err);
    });
};

const isBlackListed = (user: any): boolean => {
  return user.blacklistedData;
};

export const isResident = (user: any) => {
  if (!user.residencesInfo) return false;
  return user.residencesInfo?.length !== 0;
};

export const convertAuthorization = (
  data: any,
  options?: {
    type?: AuthorizationType;
    mark?: boolean;
    hidden?: boolean;
    guestType?: GuestType;
  }
): IAuthorization => {
  let guestType =
    options?.guestType ||
    ((data.guestsType || data.guestType) === "providers"
      ? GuestType.provider
      : data.guestsType || data.guestType || GuestType.any);

  if (data?.entriesAsProvider) {
    if (data.entriesAsProvider.indexOf(data.id) != -1) {
      guestType = GuestType.provider;
    } else if (data?.host && data.entriesAsProvider.indexOf(data.host.id) != -1) {
      guestType = GuestType.provider;
    }
  }

  return {
    id: data.id,
    authorization: data,
    mark: options?.mark ? options.mark : false,
    guestType,
    type: options?.type ? options.type : AuthorizationType.current,
    hidden: options?.hidden ? options.hidden : false,
  };
};

export const createEventOut = (
  token: string,
  initialUser: UserState,
  dispatch: any,
  config?: Config
) => {
  newEvent.createInitialEvent(token, initialUser.id).then(async (response: any) => {
    initialUser = {
      ...initialUser,
      eventId: response.data.id,
      old: {
        lastname: initialUser.lastname,
        firstname: initialUser.firstname,
        countryCode: initialUser.countryCode,
        dni: initialUser.dni,
        driverLicenseExpirationDate: initialUser.driverLicenseExpirationDate,
        workMaterials: initialUser.workMaterials,
        artCompanyName: initialUser.artCompanyName,
        artPolicyNumber: initialUser.artPolicyNumber,
        artExpirationDate: initialUser.artExpirationDate,
        criminalRecordIssueDate: initialUser.artExpirationDate,
        professionTypes: initialUser.professionTypes,
      },
    };
    if (config) {
      initialUser = { ...initialUser, config };
      if (config.userAddon) initialUser = { ...initialUser, ...config.userAddon };
    }
    if (initialUser.companions?.length > 0) {
      let companions: UserState[] = [];
      await Promise.all(
        initialUser.companions.map(async (companion) => {
          await newEvent.createInitialEvent(token, companion.id).then((response: any) => {
            companion = {
              ...companion,
              eventId: response.data.id,
              old: {
                lastname: companion.lastname,
                firstname: companion.firstname,
                countryCode: companion.countryCode,
                dni: companion.dni,
                driverLicenseExpirationDate: companion.driverLicenseExpirationDate,
                workMaterials: companion.workMaterials,
                artCompanyName: companion.artCompanyName,
                artPolicyNumber: companion.artPolicyNumber,
                artExpirationDate: companion.artExpirationDate,
                criminalRecordIssueDate: companion.artExpirationDate,
                professionTypes: companion.professionTypes,
              },
            };
            companions.push(companion);
          });
        })
      );
      initialUser.companions = companions;
    }
    dispatch(initialUser);
  });
};

const createEvent = (user: any, token: string, dispatch: any, config?: Config) => {
  newEvent
    .createInitialEvent(token, user.id)
    .then((response: any) => {
      const firstname = user.firstname === "-pending-" ? "" : user.firstname;
      const lastname = user.lastname === "-pending-" ? "" : user.lastname;
      user = {
        ...user,
        comment: "",
        permanentComment: user.comment ?? "",
        commentReject: "",
        lastname,
        firstname,
        eventId: response.data.id,
        authorizations: [],
        isResident: isResident(user),
        isBlackList: isBlackListed(user),
        old: {
          lastname,
          firstname,
          countryCode: user.countryCode,
          dni: user.dni,
          driverLicenseExpirationDate: user.driverLicenseExpirationDate || "",
          workMaterials: user.workMaterials || "",
          artCompanyName: user.artCompanyName || "",
          artPolicyNumber: user.artPolicyNumber || "",
          artExpirationDate: user.artExpirationDate || "",
          criminalRecordIssueDate: user.artExpirationDate || "",
          professionTypes: user.professionTypes || "",
        },
      };
      if (config) {
        user = { ...user, config };
        if (config.userAddon) user = { ...user, ...config.userAddon };
      }
      dispatch(user);
    })
    .catch((err) => {
      console.error(err);
    });
};
